//src/store/slices/sixDetail/sixDetailSlice.ts
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { FileEntry, FileSortDir, FileSortKey, SixDetailFilterKey, SixDetailState } from '@/types/SixDetail.types';
import { initSixDetailThunk } from '@/store/thunks/sixDetail/getInitialDetailThunk';
import { uploadFileThunk } from '@/store/thunks/sixDetail/uploadFileThunk';
import { createFolderThunk } from '@/store/thunks/sixDetail/createFolderThunk';
import { fetchActivityThunk } from '@/store/thunks/sixDetail/fetchActivityThunk';
import { fetchParticipantsThunk } from '@/store/thunks/sixDetail/fetchParticipantsThunk';
import { fetchSixDetailMetaAndFilesThunk } from '@/store/thunks/sixDetail/fetchMetaAndFilesThunk';
import { fetchFilesThunk } from '@/store/thunks/sixDetail/fetchFilesThunk';
import { toggleFileFavoriteThunk } from '@/store/thunks/sixDetail/toggleFileFavoriteThunk';
import { deleteFileThunk } from '@/store/thunks/sixDetail/deleteFileThunk';
import { deleteFolderThunk } from '@/store/thunks/sixDetail/deleteFolderThunk';
import { moveItemsThunk } from '@/store/thunks/sixDetail/moveItemsThunk';
import { extractThunkError } from '@/store/utils';

const UPLOAD_PROGRESS = 0;

const initialState: SixDetailState = {
  activeFilters: [],
  sixId: undefined,
  files: [],
  activity: [],
  participants: undefined,
  loadingFiles: false,
  loadingActivity: false,
  loadingParticipants: false,
  uploadProgress: undefined,
  error: undefined,
  meta: undefined,
  currentFolderPath: undefined,
  search: undefined,
  selectedFileIds: [],
  sortKey: 'name',
  sortDir: 'asc',
  isFileDetailModalOpen: false,
  fileDetailId: undefined,
};

function toggleFilterValue(currentFilters: SixDetailFilterKey[], nextFilter: SixDetailFilterKey): SixDetailFilterKey[] {
  if (currentFilters.includes(nextFilter)) {
    return currentFilters.filter((filterValue) => filterValue !== nextFilter);
  }
  return [...currentFilters, nextFilter];
}

export const sixDetailSlice = createSlice({
  name: 'sixDetail',
  initialState,
  reducers: {
    setUploadProgress(state, action: PayloadAction<number>) {
      state.uploadProgress = action.payload;
    },
    resetSixDetailError(state) {
      state.error = undefined;
    },
    resetSixDetailState() {
      return initialState;
    },
    setActiveFilters(state, action: PayloadAction<SixDetailFilterKey[]>) {
      state.activeFilters = action.payload;
    },
    toggleActiveFilter(state, action: PayloadAction<SixDetailFilterKey>) {
      state.activeFilters = toggleFilterValue(state.activeFilters, action.payload);
    },
    navigateToFolder(state, action: PayloadAction<string>) {
      state.currentFolderPath = action.payload;
      state.selectedFileIds = [];
    },
    navigateToBreadcrumb(state, action: PayloadAction<string>) {
      state.currentFolderPath = action.payload;
      state.selectedFileIds = [];
    },
    navigateToRoot(state) {
      state.currentFolderPath = undefined;
      state.selectedFileIds = [];
    },
    setSearchQuery(state, action: PayloadAction<string | undefined>) {
      state.search = action.payload;
    },
    setFiles(state, action: PayloadAction<FileEntry[]>) {
      state.files = action.payload;
    },
    setSelectedFileIds(state, action: PayloadAction<string[]>) {
      state.selectedFileIds = action.payload;
    },
    setFileSort(state, action: PayloadAction<{ sortKey: FileSortKey; sortDir: FileSortDir }>) {
      state.sortKey = action.payload.sortKey;
      state.sortDir = action.payload.sortDir;
    },
    openFileDetailModal(state, action: PayloadAction<string>) {
      state.isFileDetailModalOpen = true;
      state.fileDetailId = action.payload;
    },
    closeFileDetailModal(state) {
      state.isFileDetailModalOpen = false;
      state.fileDetailId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initSixDetailThunk.pending, (state) => {
        state.loadingFiles = true;
        state.loadingActivity = true;
        state.error = undefined;
      })
      .addCase(initSixDetailThunk.fulfilled, (state, action) => {
        const {
          payload: { sixId, meta, files, activity, participants },
        } = action;
        state.sixId = sixId;
        state.files = files;
        state.activity = activity;
        state.participants = participants;
        state.meta = meta;
        state.currentFolderPath = undefined;
        state.loadingFiles = false;
        state.loadingActivity = false;
      })
      .addCase(initSixDetailThunk.rejected, (state, action) => {
        state.loadingFiles = false;
        state.loadingActivity = false;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(fetchSixDetailMetaAndFilesThunk.pending, (state) => {
        state.loadingFiles = true;
        state.error = undefined;
      })
      .addCase(fetchSixDetailMetaAndFilesThunk.fulfilled, (state, action) => {
        const { meta, files, participants } = action.payload;
        state.meta = meta;
        state.files = files;
        state.participants = participants;
        state.loadingFiles = false;
      })
      .addCase(fetchSixDetailMetaAndFilesThunk.rejected, (state, action) => {
        state.loadingFiles = false;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(uploadFileThunk.pending, (state) => {
        state.uploadProgress = UPLOAD_PROGRESS;
        state.error = undefined;
      })
      .addCase(uploadFileThunk.fulfilled, (state, action) => {
        state.uploadProgress = undefined;
      })
      .addCase(uploadFileThunk.rejected, (state, action) => {
        state.uploadProgress = undefined;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(createFolderThunk.fulfilled, () => {
        // Handled by refetch — no state update needed
      })
      .addCase(createFolderThunk.rejected, (state, action) => {
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(fetchActivityThunk.pending, (state) => {
        state.loadingActivity = true;
        state.error = undefined;
      })
      .addCase(fetchActivityThunk.fulfilled, (state, action) => {
        state.activity = action.payload;
        state.loadingActivity = false;
      })
      .addCase(fetchActivityThunk.rejected, (state, action) => {
        state.loadingActivity = false;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(fetchParticipantsThunk.pending, (state) => {
        state.loadingParticipants = true;
        state.error = undefined;
      })
      .addCase(fetchParticipantsThunk.fulfilled, (state, action) => {
        state.participants = action.payload;
        state.loadingParticipants = false;
      })
      .addCase(fetchParticipantsThunk.rejected, (state, action) => {
        state.loadingParticipants = false;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(fetchFilesThunk.pending, (state) => {
        state.loadingFiles = true;
        state.error = undefined;
      })
      .addCase(fetchFilesThunk.fulfilled, (state, action) => {
        state.files = action.payload;
        state.loadingFiles = false;
      })
      .addCase(fetchFilesThunk.rejected, (state, action) => {
        state.loadingFiles = false;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(toggleFileFavoriteThunk.pending, (state) => {
        state.error = undefined;
      })
      .addCase(toggleFileFavoriteThunk.fulfilled, () => {
        // Files are already refreshed via fetchFilesThunk inside the thunk
      })
      .addCase(toggleFileFavoriteThunk.rejected, (state, action) => {
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(deleteFileThunk.pending, (state) => {
        state.error = undefined;
      })
      .addCase(deleteFileThunk.fulfilled, (state) => {
        state.selectedFileIds = [];
      })
      .addCase(deleteFileThunk.rejected, (state, action) => {
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(deleteFolderThunk.pending, (state) => {
        state.error = undefined;
      })
      .addCase(deleteFolderThunk.fulfilled, (state) => {
        state.selectedFileIds = [];
      })
      .addCase(deleteFolderThunk.rejected, (state, action) => {
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(moveItemsThunk.pending, (state) => {
        state.error = undefined;
      })
      .addCase(moveItemsThunk.fulfilled, (state) => {
        state.selectedFileIds = [];
      })
      .addCase(moveItemsThunk.rejected, (state, action) => {
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      });
  },
});

const { actions } = sixDetailSlice;

export const {
  setUploadProgress,
  resetSixDetailError,
  resetSixDetailState,
  setActiveFilters,
  toggleActiveFilter,
  navigateToFolder,
  navigateToBreadcrumb,
  navigateToRoot,
  setSearchQuery,
  setFiles,
  setSelectedFileIds,
  setFileSort,
  openFileDetailModal,
  closeFileDetailModal,
} = actions;

export default sixDetailSlice.reducer;
